import type { GameState } from '../state';
import { D, add, mul, div, pow, max, type Decimal } from '../math/num';
import { getOverlord, OVERLORDS, type OverlordDef } from '../data/overlords';
import { waveHp, waveDmg, waveScrap, waveEnemyCount, type ActiveEnemy } from './spawn';
import { unlockDrones } from './drones';
import { unlockFoundry } from './foundry';
import { unlockYard } from './yard';
import { unlockSmelter } from './smelter';
import { unlockRails } from './rails';
import { unlockTakeover } from './takeover';
import { unlockGrid } from './grid';
import { unlockDistricts } from './districts';
import { unlockHarvest } from './harvest';
import { MINI_KILLS_PER_LEVEL, OVERLORD_MINI_DEFS } from '../data/pets';
import { grantPet } from './pets';
import type { EmitFn } from './events';

/**
 * Overlord duels. Selecting an overlord swaps the arena from waves to a single
 * giant boss; it is a fight to the death. Victory marks it beaten (revealing
 * the next overlord, authorizing the Ascension it gates, and unlocking its
 * feature); losing runs the normal defeat countdown. Beaten overlords can be
 * re-fought for fun, with no extra reward.
 *
 * Stats derive from the same wave curves the horde uses, anchored at the
 * overlord's recommended wave, so balance tuning stays in one place.
 */

export const OVERLORD_ENEMY_ID = 'overlord';

/** ~this many waves' worth of total HP: a real fight, not a speed bump. */
export const OVERLORD_HP_WAVES = 6;
/** Damage relative to the recommended wave's total incoming. */
export const OVERLORD_DMG_MULT = 1.2;
/** Post-victory breather before waves resume. */
export const VICTORY_PAUSE_MS = 4_000;
/** Pre-duel hold: the arena loads, the WARNING plays, nobody swings yet. */
export const DUEL_INTRO_MS = 3_000;

export function overlordMaxHp(def: OverlordDef): Decimal {
  const perWave = mul(waveHp(def.recWave), D(waveEnemyCount(def.recWave)));
  return mul(perWave, D(OVERLORD_HP_WAVES));
}

export function overlordDps(def: OverlordDef): Decimal {
  return mul(mul(waveDmg(def.recWave), D(waveEnemyCount(def.recWave))), D(OVERLORD_DMG_MULT));
}

/** DPS that kills it in ~45s — the "recommended DPS" shown on the dossier. */
export function overlordRecDps(def: OverlordDef): Decimal {
  return div(overlordMaxHp(def), D(45));
}

/** First-kill bounty (re-fights pay nothing). */
export function overlordScrapReward(def: OverlordDef): Decimal {
  return mul(waveScrap(def.recWave), D(60));
}
export function overlordComponentReward(def: OverlordDef): Decimal {
  return max(D(10), mul(pow(D(1.08), def.recWave - 1), D(10)));
}

export function isOverlordDefeated(state: GameState, id: string): boolean {
  return state.overlords.defeated.includes(id);
}

/** The first undefeated overlord — the one card the dossier unveils. */
export function currentChallenge(state: GameState): OverlordDef | null {
  return OVERLORDS.find((o) => !isOverlordDefeated(state, o.id)) ?? null;
}

/** Fightable = already beaten (for fun) or the current challenge. Later
 *  overlords stay hidden until the ladder reaches them. */
export function isOverlordAvailable(state: GameState, id: string): boolean {
  return isOverlordDefeated(state, id) || currentChallenge(state)?.id === id;
}

function makeOverlordEnemy(def: OverlordDef): ActiveEnemy {
  const hp = overlordMaxHp(def);
  return {
    id: OVERLORD_ENEMY_ID,
    visual: { modelId: def.modelId, paletteId: 'pal.factory', loadoutId: 'ld.bare', modifierIds: [] },
    name: def.name,
    hp,
    maxHp: hp,
    dmgPerSec: overlordDps(def),
    scrapReward: D(0), // paid as a first-kill bounty in winOverlordFight
    componentReward: D(0),
    isBoss: true,
    size: 2.4,
    resist: { kinetic: 1, energy: 1, corrosive: 1 },
  };
}

/** Load the overlord's arena: the horde steps aside, the duel begins. */
export function startOverlordFight(state: GameState, id: string, emit?: EmitFn): boolean {
  const def = getOverlord(id);
  if (!def) return false;
  if (!isOverlordAvailable(state, id)) return false;
  const c = state.combat;
  if (c.overlordId || c.pauseKind) return false;
  c.overlordId = id;
  c.enemies = [makeOverlordEnemy(def)];
  c.enrageMs = 0; // the duel starts calm; it will not stay that way
  // hold combat for the intro: the overlord gets its entrance, then the bell
  c.pauseKind = 'duelIntro';
  c.pauseUntilMs = state.elapsedMs + DUEL_INTRO_MS;
  emit?.({ kind: 'spawn', targetId: OVERLORD_ENEMY_ID });
  return true;
}

/** The overlord fell: mark it beaten, pay the first-kill bounty, unlock its
 *  feature, and give the arena a short breather before waves resume. */
export function winOverlordFight(state: GameState, emit?: EmitFn): void {
  const id = state.combat.overlordId;
  const def = id ? getOverlord(id) : undefined;
  state.combat.overlordId = null;
  state.combat.pauseKind = 'victory';
  state.combat.pauseUntilMs = state.elapsedMs + VICTORY_PAUSE_MS;
  if (!def) return;

  // every kill counts toward the overlord's mini: each 5th kill grants a
  // level into the shared pet collection (up to the drop cap; the Yard
  // trains further). grantPet auto-deploys a first earn into a free slot.
  const kills = (state.overlords.defeats[def.id] ?? 0) + 1;
  state.overlords.defeats[def.id] = kills;
  if (kills % MINI_KILLS_PER_LEVEL === 0 && OVERLORD_MINI_DEFS[def.id]) {
    grantPet(state, def.id);
  }

  const firstKill = !isOverlordDefeated(state, def.id);
  if (firstKill) {
    state.overlords.defeated.push(def.id);
    const scrap = overlordScrapReward(def);
    state.wallet.scrap = add(state.wallet.scrap, scrap);
    state.meta.lifetimeScrap = add(state.meta.lifetimeScrap, scrap);
    state.wallet.components = add(state.wallet.components, overlordComponentReward(def));
    if (def.feature === 'drones') unlockDrones(state);
    if (def.feature === 'pets') state.pets.unlocked = true;
    if (def.feature === 'yard') unlockYard(state);
    if (def.feature === 'foundry') unlockFoundry(state);
    if (def.feature === 'smelter') unlockSmelter(state);
    if (def.feature === 'rails') unlockRails(state);
    if (def.feature === 'takeover') unlockTakeover(state);
    if (def.feature === 'grid') unlockGrid(state);
    if (def.feature === 'districts') unlockDistricts(state);
    if (def.feature === 'harvest') unlockHarvest(state);
  }
  emit?.({
    kind: 'overlordDefeated',
    overlordId: def.id,
    name: def.name,
    firstKill,
    unlockedLabel: firstKill ? def.unlocksFeatureLabel : null,
  });
}

/** DEV/admin shortcut: defeat the current challenge through the real victory
 *  path (bounty + unlocks), minus the arena breather. Testing only. */
export function devDefeatChallenge(state: GameState): void {
  const ch = currentChallenge(state);
  if (!ch) return;
  state.combat.overlordId = ch.id;
  winOverlordFight(state);
  state.combat.pauseKind = null;
}
